/**
 * Waitlist service for handling user signups
 */

/**
 * Add a user to the waitlist
 * @param {Object} signupData - The signup data
 * @param {string} signupData.email - User's email address
 * @param {string} signupData.firstName - User's first name (optional)
 * @param {string} signupData.lastName - User's last name (optional)
 * @param {string} signupData.fullName - User's full name (optional)
 * @param {string} signupData.signupSource - Where the user signed up from
 * @param {string} signupData.featureInterest - Specific feature they're interested in (optional)
 * @param {Object} signupData.metadata - Additional metadata (optional)
 * @returns {Promise<Object>} API response
 */
export const addToWaitlist = async (signupData) => {
  try {
    // Use the same pattern as other services
    const API_URL = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5000/api' 
      : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
    
    const response = await fetch(`${API_URL}/waitlist/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: signupData.email,
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        fullName: signupData.fullName,
        signupSource: signupData.signupSource || 'other',
        featureInterest: signupData.featureInterest,
        userAgent: navigator.userAgent,
        metadata: signupData.metadata
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('Error adding to waitlist:', error);
    throw error;
  }
};

/**
 * Add a user to waitlist for feature launch notification
 * @param {string} userEmail - User's email address
 * @param {string} userName - User's name (optional)
 * @param {string} featureName - Name of the feature
 * @returns {Promise<Object>} API response
 */
export const addFeatureInterest = async (userEmail, userName, featureName) => {
  return addToWaitlist({
    email: userEmail,
    fullName: userName,
    signupSource: 'buyer_graph_feature',
    featureInterest: featureName,
    metadata: {
      source: 'feature_request',
      requestedFeature: featureName
    }
  });
};

/**
 * Add a user to waitlist from the main waitlist page
 * @param {string} name - User's full name
 * @param {string} email - User's email address
 * @returns {Promise<Object>} API response
 */
export const addFromWaitlistPage = async (name, email) => {
  return addToWaitlist({
    email: email,
    fullName: name,
    signupSource: 'waitlist_page',
    metadata: {
      source: 'waitlist_page'
    }
  });
};

/**
 * Get waitlist statistics (admin only)
 * @returns {Promise<Object>} Waitlist statistics
 */
export const getWaitlistStats = async () => {
  try {
    const API_URL = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5000/api' 
      : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
    
    const response = await fetch(`${API_URL}/waitlist/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('Error fetching waitlist stats:', error);
    throw error;
  }
};
